import { marked } from "marked"
import { memo, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Item } from "../../../../../types/enum/Item"
import { getPkmFromPortraitSrc } from "../../../../../utils/avatar_server"
import { clamp } from "../../../../../utils/number"
import { ItemDetailTooltip } from "../../../game/components/item-detail"
import { addIconsToHtml } from "../../utils/descriptions"
import { GamePokemonDetailTooltip } from "../game/game-pokemon-detail"
import "./patch-summary.css"
import { type PatchInfo } from "../../../../../config/game/patches"

interface PatchSummaryProps {
  patch?: PatchInfo
  version?: string
  showHeader?: boolean
}

interface MidpatchNote {
  version: string
  html: string
}

function fetchMarkdown(
  url: string,
  headingLevelAdjustment: number = 0
): Promise<string> {
  return fetch(url)
    .then((res) => res.text())
    .then((md) => {
      marked.use({
        renderer: {
          heading({ tokens, depth }) {
            const newDepth = clamp(depth + headingLevelAdjustment, 1, 6)
            const text = this.parser.parseInline(tokens)
            return `<h${newDepth}>${text}</h${newDepth}>`
          },
          image({ href, title, text }) {
            const titleAttr = title ? ` title="${title}"` : ""
            let tooltipId = ""
            let content = ""
            if (href.startsWith("/assets/portraits/")) {
              tooltipId = "game-pokemon-detail-tooltip"
              content = getPkmFromPortraitSrc(href)?.name || ""
            } else if (href.startsWith("/assets/item/")) {
              tooltipId = "item-detail-tooltip"
              const itemNameMatch = href.match(/\/assets\/item\/(\w+)\.png/)
              if (itemNameMatch && itemNameMatch[1] in Item) {
                content = Item[itemNameMatch[1]]
              }
            }
            return `<img src="${href}" alt="${text}"${titleAttr} ${tooltipId && `data-tooltip-id="${tooltipId}"`} data-tooltip-content="${content}" />`
          }
        }
      })
      return marked.parse(md)
    })
}

export const PatchSummary = memo(
  ({ version }: PatchSummaryProps) => {
    const { t } = useTranslation()
    const [patchContent, setPatchContent] = useState<string>()
    const [fullPatchNotes, setFullPatchNotes] = useState<string>()
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
      setIsLoading(true)

      // Fetch both summary and full patch notes
      const fetchSummary = fetchMarkdown(
        `/changelog/summary/summary-${version}.md`
      )
      const fetchFullNotes = fetchMarkdown(`/changelog/patch-${version}.md`, 2)

      Promise.all([fetchSummary, fetchFullNotes])
        .then(([summaryParsed, fullNotesParsed]) => {
          setPatchContent(addIconsToHtml(summaryParsed))
          setFullPatchNotes(addIconsToHtml(fullNotesParsed))
        })
        .catch(() => {
          const fallbackContent = `<h2>Patch ${version}</h2><p>Changelog not available</p>`
          const fallbackNotes = "<p>Patch notes not available</p>"

          setPatchContent(addIconsToHtml(fallbackContent))
          setFullPatchNotes(addIconsToHtml(fallbackNotes))
        })
        .finally(() => {
          setIsLoading(false)
        })
    }, [version])

    const patchContentHtml = useMemo(
      () => ({ __html: patchContent || "" }),
      [patchContent]
    )
    const fullPatchNotesHtml = useMemo(
      () => ({ __html: fullPatchNotes || "" }),
      [fullPatchNotes]
    )

    return (
      <div className="patch-summary">
        {isLoading ? (
          <p>{t("loading")}...</p>
        ) : (
          <>
            <div
              className="patch-content"
              dangerouslySetInnerHTML={patchContentHtml}
            ></div>
            <hr />
            {fullPatchNotes && (
              <>
                <h2>{t("full_patch_notes")}</h2>
                <div
                  className="full-patch-notes"
                  dangerouslySetInnerHTML={fullPatchNotesHtml}
                ></div>
              </>
            )}
          </>
        )}
        <GamePokemonDetailTooltip origin="patchnotes" />
        <ItemDetailTooltip />
      </div>
    )
  },
  (prevProps, nextProps) => prevProps.version === nextProps.version
)
